import { useCallback, useEffect, useState } from "react";
import api from "../Utils/Axios";

const LAST_SEEN_KEYS = {
  admin: "adminChatLastSeenAt",
  hospital: "hospitalChatLastSeenAt",
};

const getLastSeen = (role) => {
  if (typeof window === "undefined") return 0;
  const key = LAST_SEEN_KEYS[role];
  const value = Number(localStorage.getItem(key) || 0);
  return Number.isFinite(value) ? value : 0;
};

const setLastSeen = (role, value) => {
  if (typeof window === "undefined") return;
  const key = LAST_SEEN_KEYS[role];
  localStorage.setItem(key, String(value));
};

const messageTimeMs = (sentAt) => {
  const ms = new Date(sentAt || 0).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const getAdminUnread = (chats, lastSeenAt) =>
  (chats || []).reduce((sum, chat) => {
    const count = (chat?.messages || []).filter(
      (msg) => msg?.senderRole === "hospital" && messageTimeMs(msg?.sentAt) > lastSeenAt
    ).length;
    return sum + count;
  }, 0);

const getHospitalUnread = (chat, lastSeenAt) =>
  (chat?.messages || []).filter(
    (msg) => msg?.senderRole === "admin" && messageTimeMs(msg?.sentAt) > lastSeenAt
  ).length;

const useChatNotifications = (role, pollMs = 7000) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!role) return;
    setLoading(true);
    try {
      const lastSeenAt = getLastSeen(role);
      if (role === "admin") {
        const res = await api.get("/api/chat/admin/all");
        const count = getAdminUnread(res.data?.chats || [], lastSeenAt);
        setUnreadCount(count);
      } else {
        const res = await api.get("/api/chat/hospital/me");
        const count = getHospitalUnread(res.data?.chat, lastSeenAt);
        setUnreadCount(count);
      }
    } catch (_err) {
      // Avoid noisy toasts for background polling.
    } finally {
      setLoading(false);
    }
  }, [role]);

  const markAsSeen = useCallback(
    (at = Date.now()) => {
      if (!role) return;
      setLastSeen(role, at);
      setUnreadCount(0);
    },
    [role]
  );

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, pollMs);
    return () => clearInterval(timer);
  }, [refresh, pollMs]);

  return { unreadCount, loading, refresh, markAsSeen };
};

export default useChatNotifications;
